import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Dropdown prodi yang bisa dicari (ganti <Select> biasa utk daftar ~44 prodi).
// Dibangun dari Popover + Command (cmdk) yang sudah ada di ui/ -- tanpa dependensi baru.
// Pencarian cmdk bawaan: case-insensitive & fuzzy terhadap prop `value` tiap CommandItem,
// di sini diisi "nama_prodi [jenjang] jenjang" supaya cocok dgn nama maupun label jenjang.
export default function ProdiSelect({
    prodi = [],
    value,
    onValueChange,
    placeholder = "Pilih prodi",
    searchPlaceholder = "Cari nama prodi...",
    emptyText = "Prodi tidak ditemukan.",
    showJenjang = true,
    includeAll = false,
    allLabel = "Semua Prodi",
    allValue = "all",
    disabled = false,
    className,
    contentClassName,
    testId,
}) {
    const [open, setOpen] = React.useState(false);

    const labelFor = React.useCallback(
        (p) => (showJenjang ? `[${p.jenjang}] ${p.nama_prodi}` : p.nama_prodi),
        [showJenjang],
    );

    const selectedLabel = React.useMemo(() => {
        if (includeAll && value === allValue) return allLabel;
        const found = prodi.find((p) => p.id === value);
        return found ? labelFor(found) : "";
    }, [prodi, value, includeAll, allValue, allLabel, labelFor]);

    const choose = (v) => {
        onValueChange(v);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    data-testid={testId}
                    className={cn(
                        "w-full justify-between font-normal",
                        !selectedLabel && "text-muted-foreground",
                        className,
                    )}
                >
                    <span className="truncate">{selectedLabel || placeholder}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className={cn("w-[min(320px,calc(100vw-2rem))] p-0", contentClassName)}
            >
                <Command>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {includeAll && (
                                <CommandItem value={allLabel} onSelect={() => choose(allValue)}>
                                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === allValue ? "opacity-100" : "opacity-0")} />
                                    <span className="truncate">{allLabel}</span>
                                </CommandItem>
                            )}
                            {prodi.map((p) => (
                                <CommandItem
                                    key={p.id}
                                    value={`${p.nama_prodi} [${p.jenjang}] ${p.jenjang}`}
                                    onSelect={() => choose(p.id)}
                                >
                                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === p.id ? "opacity-100" : "opacity-0")} />
                                    <span className="truncate">{labelFor(p)}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
